// 한글 초성 검색 유틸.
// 'ㅎㄱㄷ' 로 '홍길동' 을, 조합 중인 '홍ㄱ' / '홍기' 로도 '홍길동' 을 찾게 한다.
//
// piano-backend/utils/hangul.js 와 같은 로직이다. 두 앱이 워크스페이스가 아니고
// CRA 가 src/ 밖을 import 하지 못해 부득이 복제했다. 한쪽을 고치면 다른 쪽도 맞춰야 한다.

const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const JUNG = ['ㅏ','ㅐ','ㅑ','ㅒ','ㅓ','ㅔ','ㅕ','ㅖ','ㅗ','ㅘ','ㅙ','ㅚ','ㅛ','ㅜ','ㅝ','ㅞ','ㅟ','ㅠ','ㅡ','ㅢ','ㅣ'];
const JONG = ['','ㄱ','ㄲ','ㄳ','ㄴ','ㄵ','ㄶ','ㄷ','ㄹ','ㄺ','ㄻ','ㄼ','ㄽ','ㄾ','ㄿ','ㅀ','ㅁ','ㅂ','ㅄ','ㅅ','ㅆ','ㅇ','ㅈ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];

// 완성형 한글 음절 영역
const SYLLABLE_BASE = 0xAC00;
const SYLLABLE_LAST = 0xD7A3;

const CHO_SET = new Set(CHO);

// 겹받침을 이루는 첫 자모. '갑' 까지 친 상태에서 '값' 을 찾을 수 있게 한다.
const JONG_HEAD = {
  'ㄳ': 'ㄱ', 'ㄵ': 'ㄴ', 'ㄶ': 'ㄴ', 'ㄺ': 'ㄹ', 'ㄻ': 'ㄹ', 'ㄼ': 'ㄹ',
  'ㄽ': 'ㄹ', 'ㄾ': 'ㄹ', 'ㄿ': 'ㄹ', 'ㅀ': 'ㄹ', 'ㅄ': 'ㅂ',
};

// 한 글자를 초성/중성/종성으로 분해. 한글 음절이 아니면 null.
function decompose(ch) {
  const code = ch.charCodeAt(0);
  if (code < SYLLABLE_BASE || code > SYLLABLE_LAST) return null;
  const index = code - SYLLABLE_BASE;
  return {
    cho: CHO[Math.floor(index / 588)],
    jung: JUNG[Math.floor((index % 588) / 28)],
    jong: JONG[index % 28],
  };
}

// 검색 비교용 정규화. iOS 입력이 NFD 로 들어오는 경우가 있어 NFC 로 맞추고,
// 대소문자와 공백 차이는 무시한다(아이디 검색의 기존 동작 유지).
function normalize(value) {
  return String(value ?? '').normalize('NFC').toLowerCase().replace(/\s+/g, '');
}

// 대상 한 글자가 질의 한 글자에 부합하는지.
// isLast=true 면 조합 중인 미완성 음절('기' → '길')까지 허용한다.
function charMatches(target, q, isLast) {
  if (target === q) return true;

  const t = decompose(target);
  if (!t) return false;

  // 홀 자음은 초성으로 비교한다. 된소리(ㄲ/ㄸ/ㅃ/ㅆ/ㅉ)는 엄격히 구분한다.
  if (CHO_SET.has(q)) return q === t.cho;

  // 완성 음절이 중간에 오면 정확히 일치해야 한다(위 target === q 에서 이미 걸러짐).
  if (!isLast) return false;

  const s = decompose(q);
  if (!s) return false;
  if (s.cho !== t.cho || s.jung !== t.jung) return false;
  if (s.jong === '') return true;
  return s.jong === t.jong || s.jong === JONG_HEAD[t.jong];
}

// text 안에 query 가 (초성·미완성 음절 포함) 부분 문자열로 등장하는지.
export function matchesSearch(text, query) {
  const q = normalize(query);
  if (!q) return true;

  const target = normalize(text);
  const lastIndex = q.length - 1;

  for (let start = 0; start + q.length <= target.length; start++) {
    let ok = true;
    for (let i = 0; i < q.length; i++) {
      if (!charMatches(target[start + i], q[i], i === lastIndex)) { ok = false; break; }
    }
    if (ok) return true;
  }
  return false;
}

// 이름·아이디 등 여러 필드 중 하나라도 맞으면 true.
export function matchesAnySearch(texts, query) {
  const q = normalize(query);
  if (!q) return true;
  return texts.some((t) => t && matchesSearch(t, q));
}
